import style from "./BillingForm.module.css";
import Spinner from "@/features/forms/components/Spinner";
import QueryError from "@/features/admin/components/QueryError";

import { useSubscriptionDetails } from "../../hooks/useSubscriptionDetails";
import { useSubscribe } from "../../hooks/useSubscribe";

import SettingsSection from "@/features/settings/components/settingsSection/SettingsSection";

import Button from "@/ui/button/Button";
import Icon from "@/ui/icon/Icon";

import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { formatPrice, formatShortDate } from "@/i18n/format";
import { useCurrentLanguage } from "@/i18n/useLocalized";
import {
	deriveAccessWindow,
	deriveRenewalNotice,
	parseApiDate,
} from "../../deriveBilling";
import { derivePlans } from "../../plans";
import PlanPicker from "../planPicker/PlanPicker";
import { useUpdatePaymentMethod } from "../../hooks/useUpdatePaymentMethod";
import { useChangePlan } from "../../hooks/useChangePlan";
import { usePriceInfo } from "../../hooks/usePriceInfo";
import { useState, useEffect } from "react";
import { successToast } from "@/ui/toast/Toast";
import { useApiSend } from "@/hooks/useApi";
import { enableAutoRenew, disableAutoRenew } from "../../api";
import { useQueryClient } from "@tanstack/react-query";

function BillingForm({ user }) {
	const { t } = useTranslation();
	const { subscription, isPending, isError, error } =
		useSubscriptionDetails();

	const { subscribe, isPending: isSubscribing } = useSubscribe();
	const { updatePaymentMethod, isPending: isUpdatingCard } =
		useUpdatePaymentMethod();
	const { changePlan, isPending: isChangingPlan } = useChangePlan();
	const { priceInfo } = usePriceInfo();
	const [selectedPlanId, setSelectedPlanId] = useState(null);
	const queryClient = useQueryClient();
	const language = useCurrentLanguage();
	const [autoRenew, setAutoRenew] = useState(
		subscription?.auto_renew || false
	);

	const { mutateAsync: enableAutoRenewMutation, isPending: isEnabling } =
		useApiSend({
			fn: enableAutoRenew,
		});

	const { mutateAsync: disableAutoRenewMutation, isPending: isDisabling } =
		useApiSend({
			fn: disableAutoRenew,
		});

	useEffect(() => {
		if (subscription?.auto_renew !== undefined)
			setAutoRenew(subscription.auto_renew);
	}, [subscription?.auto_renew]);

	useEffect(() => {
		if (subscription?.payment_plan)
			setSelectedPlanId(subscription.payment_plan);
	}, [subscription?.payment_plan]);

	const handleChangePlan = async () => {
		try {
			await changePlan(selectedPlanId);
			successToast(i18n.t("payments:billing.change.saved"));
		} catch (error) {
			// Error is handled by useApiSend
		}
	};

	const handleAutoRenewToggle = async (checked) => {
		try {
			if (checked) {
				await enableAutoRenewMutation();
				successToast(i18n.t("payments:renewal.scheduled.title"));
			} else {
				await disableAutoRenewMutation();
				successToast(i18n.t("payments:billing.autoRenew.off"));
			}
			setAutoRenew(checked);
			queryClient.invalidateQueries({
				queryKey: ["whoami", "subscription-details"],
			});
		} catch (error) {
			// Error is handled by useApiSend
		}
	};

	if (isPending) return <Spinner />;
	if (isError) return <QueryError error={error} />;

	const startDate = new Date(subscription?.start_date);
	const { endsOn, daysLeft, progressPercent } =
		deriveAccessWindow(subscription);
	const renewalNotice = deriveRenewalNotice(subscription, { t, language });
	const canChangePlan = subscription?.can_change_plan !== false;

	const plans = derivePlans(priceInfo);
	const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
	const isCurrentPlan =
		!selectedPlanId || selectedPlanId === subscription?.payment_plan;
	const nextRenewal = parseApiDate(subscription?.next_renewal_date);
	const renewsOn = nextRenewal ? formatShortDate(nextRenewal, language) : null;

	return (
		<div>
			<SettingsSection
				title={t("payments:billing.plan.title")}
				description={t("payments:billing.plan.description")}
			>
				<div
					className={`${style.plan} ${
						subscription?.is_active ? style.preferred : ""
					}`}
				>
					<div className={style.planHeader}>
						<h3>
							{subscription?.is_active
								? t("payments:plans.premium")
								: t("payments:plans.free")}
						</h3>
					</div>
					<div className={style.planBody}>
						{subscription?.is_active && (
							<>
								<div className={style.progressInfo}>
									<div className={style.info}>
										<div className={style.icoHolder}>
											<Icon icon="fa-calendar" />
										</div>
										<span>{t("payments:billing.plan.start")}</span>
										<span>{formatShortDate(startDate, language)}</span>
									</div>
									<div className={style.info}>
										<div className={style.icoHolder}>
											<Icon icon="fa-flag-checkered" />
										</div>
										<span>{t("payments:billing.plan.end")}</span>
										<span>{formatShortDate(endsOn, language)}</span>
									</div>
								</div>
								<div className={style.progress}>
									<div
										className={style.progressFill}
										style={{ width: `${progressPercent}%` }}
									></div>
									<span>{progressPercent}%</span>
								</div>
							</>
						)}
					</div>
					<div className={style.planFooter}>
						{subscription?.is_active && (
							<div className={style.daysLeft}>
								<span>
									{t("payments:billing.plan.daysLeft", {
										count: daysLeft,
									})}{" "}
								</span>
								<div className={style.days}>
									{daysLeft}{" "}
									<span>
										{t("payments:billing.plan.days", {
											count: daysLeft,
										})}
									</span>
								</div>
							</div>
						)}
						<div className={style.buttonHolder}>
							<Button
								className={style.btn}
								onClick={() => subscribe(user.payment_plan)}
							>
								{isSubscribing ? (
									<Spinner />
								) : subscription?.is_active ? (
									t("payments:billing.plan.extend")
								) : (
									t("payments:billing.plan.subscribe")
								)}
							</Button>
						</div>
					</div>
				</div>
				{subscription?.is_active && (
					<div className={style.autoRenew}>
						<label className={style.toggle}>
							<span>{t("payments:billing.autoRenew.label")}</span>
							<input
								type="checkbox"
								checked={autoRenew}
								onChange={(e) => {
									handleAutoRenewToggle(e.target.checked);
								}}
							/>
							<span className={style.slider}></span>
						</label>
						<p className={style.autoRenewInfo}>
							{t("payments:billing.autoRenew.info")}
						</p>
						{renewalNotice && (
							<div
								className={`${style.notice} ${
									style[renewalNotice.tone]
								}`}
							>
								<h4 className={style.noticeTitle}>
									{renewalNotice.title}
								</h4>
								<p className={style.noticeBody}>
									{renewalNotice.body}
								</p>
								{renewalNotice.action && (
									<Button
										className={style.noticeBtn}
										onClick={updatePaymentMethod}
									>
										{isUpdatingCard ? (
											<Spinner />
										) : (
											renewalNotice.action.label
										)}
									</Button>
								)}
							</div>
						)}
					</div>
				)}
			</SettingsSection>
			{subscription?.is_active && canChangePlan && (
				<SettingsSection
					title={t("payments:billing.change.title")}
					description={t("payments:billing.change.description")}
				>
					<PlanPicker
						plans={plans}
						selectedId={selectedPlanId}
						currentId={subscription?.payment_plan}
						onSelect={setSelectedPlanId}
					/>
					<div className={style.planChange}>
						<p className={style.planChangeInfo}>
							{isCurrentPlan
								? t("payments:billing.change.current")
								: selectedPlan && renewsOn
									? t("payments:billing.change.chargeFrom", {
											date: renewsOn,
											amount: formatPrice(
												selectedPlan.totalPrice,
												selectedPlan.currency,
												language
											),
										})
									: t("payments:billing.change.fromNextRenewal")}
						</p>
						<Button
							className={style.planBtn}
							onClick={handleChangePlan}
							disabled={isCurrentPlan || isChangingPlan}
						>
							{isChangingPlan ? (
								<Spinner />
							) : (
								<>
									<span>{t("payments:billing.change.save")}</span>
									<Icon icon="fa-box" />
								</>
							)}
						</Button>
					</div>
				</SettingsSection>
			)}
		</div>
	);
}

export default BillingForm;
